import axios from 'axios';
import { v4 as uuid } from 'uuid';

const activityClient = axios.create({ baseURL: process.env.REACT_APP_API_ENDPOINT });
const MAX_EVENTS_PER_MINUTE = 20;
const recentEventTimes = [];
let configured = false;

const getRequestId = (responseOrConfig = {}) => {
    const response = responseOrConfig.response || responseOrConfig;
    return (
        response.headers?.['x-request-id'] ||
        response.config?.activity?.requestId ||
        responseOrConfig.config?.activity?.requestId ||
        uuid()
    );
};

const getRoute = (url = '') => {
    try {
        return new URL(url, window.location.origin).pathname;
    } catch {
        return `${url}`.split('?')[0];
    }
};

const getError = (error = {}) => ({
    name: error.name || error.data?.name || 'Error',
    message: error.message || error.data?.message || error.statusText || 'Unexpected application error',
    code: error.code,
    stack: error.stack,
});

const withinRateLimit = () => {
    const cutoff = Date.now() - 60 * 1000;
    while (recentEventTimes.length && recentEventTimes[0] < cutoff) recentEventTimes.shift();
    if (recentEventTimes.length >= MAX_EVENTS_PER_MINUTE) return false;
    recentEventTimes.push(Date.now());
    return true;
};

export const captureActivity = async (activity) => {
    const token = localStorage.getItem('auth_token');
    if (!token || !withinRateLimit()) return false;

    try {
        await activityClient.post(
            '/activities',
            {
                ...activity,
                event_id: activity.event_id || uuid(),
                app_version: process.env.REACT_APP_GIT_TAG,
                source: 'frontend',
                context: {
                    page: window.location.pathname,
                    platform: process.env.REACT_APP_KIND || process.env.REACT_APP_PLATFORM,
                    browser: window.navigator.userAgent,
                    language: window.navigator.language,
                    online: window.navigator.onLine,
                    ...activity.context,
                },
            },
            {
                headers: { Authorization: `Bearer ${token}` },
            }
        );
        return true;
    } catch {
        // Telemetry must never interrupt a user's workflow or recursively report itself.
        return false;
    }
};

export const captureApiFailure = (error = {}) => {
    const response = error.response || {};
    const config = error.config || response.config || {};
    const status = Number(response.status) || undefined;

    void captureActivity({
        request_id: getRequestId(error),
        level: 'error',
        event: 'api.request_failed',
        route: getRoute(config.url),
        method: config.method,
        status_code: status,
        duration_ms: config.activity?.startedAt ? Date.now() - config.activity.startedAt : undefined,
        error: getError(error.message ? error : { ...response, message: response.data?.message }),
        context: { operation: 'api_request' },
    });
};

export const captureApiSuccess = (response = {}) => {
    const config = response.config || {};
    const route = getRoute(config.url);
    if (route !== '/create-course') return;

    void captureActivity({
        request_id: getRequestId(response),
        level: 'info',
        event: 'course.create_completed',
        route,
        method: config.method,
        status_code: response.status,
        duration_ms: config.activity?.startedAt ? Date.now() - config.activity.startedAt : undefined,
        context: { operation: 'create_course' },
    });
};

export const captureRenderError = (error, componentStack) => {
    void captureActivity({
        level: 'error',
        event: 'ui.render_failed',
        error: getError(error),
        context: {
            component: `${componentStack || ''}`.slice(0, 160),
            operation: 'react_render',
        },
    });
};

export const configureActivityTracking = () => {
    if (configured) return;
    configured = true;

    axios.interceptors.request.use((config) => {
        config.activity = {
            requestId: uuid(),
            startedAt: Date.now(),
        };
        return config;
    });

    window.addEventListener('error', (event) => {
        void captureActivity({
            level: 'error',
            event: 'ui.uncaught_error',
            error: getError(event.error || { message: event.message }),
            context: { operation: 'window_error' },
        });
    });

    window.addEventListener('unhandledrejection', (event) => {
        const reason = event.reason;
        void captureActivity({
            level: 'error',
            event: 'ui.unhandled_rejection',
            error: getError(reason instanceof Error ? reason : { message: `${reason || 'Unhandled rejection'}` }),
            context: { operation: 'unhandled_rejection' },
        });
    });
};
